/**
 * The builder helper: a docs-grounded assistant in a floating launcher on the
 * workflow editor AND on the gallery, where someone who doesn't know what to
 * build is standing. It reads the how-to guides, the catalogs, and whatever
 * draft is on screen; it proposes workflows but never saves one.
 *
 * `open` / `onOpenChange` are optional so a page can open it from its own
 * affordance — the gallery's "say it in your own words" row — instead of
 * making people find the bubble.
 */
import { Chat, FloatingLauncher } from '@servicecompressionllc/sc-design-system';
import { Bot, MessageCircle } from '@servicecompressionllc/sc-design-system/icons';
import type { BuilderDefinition } from '../../lib/types.js';
import { SummarySentence } from '../workflowBuilder/SummarySentence.js';
import { summarize } from '../workflowBuilder/summarize.js';
import { useBuilderHelperLogic, type HelperContext } from './useBuilderHelperLogic.js';

export const BuilderHelper = ({
  open,
  onOpenChange,
  onApply,
  onDraftPreview,
  onBusyChange,
  ...context
}: HelperContext & {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Takes a proposal into the draft on screen — name, trigger, steps and any
   * DM channels it wants created. The page owns the write. */
  onApply?: (proposal: {
    name?: string;
    trigger?: string;
    definition: BuilderDefinition;
    pendingChannels?: { ref: string; targetUserId: string }[];
  }) => void;
  /** Live, partial state during the interview. The page passes this only when
   * a half-built workflow on screen can't do any harm. */
  onDraftPreview?: (draft: {
    name?: string;
    trigger?: string;
    definition?: unknown;
    pendingChannels?: { ref: string; targetUserId: string }[];
  }) => void;
  /** Told when the helper is working, so the page can say so. */
  onBusyChange?: (busy: boolean) => void;
}) => {
  const logic = useBuilderHelperLogic(context, onDraftPreview, onBusyChange);
  return (
    <FloatingLauncher
      label="the helper"
      storageKey="builder-helper"
      corner="bottom-right"
      open={open}
      onOpenChange={onOpenChange}
    >
      <FloatingLauncher.Panel aria-label="Builder helper">
        <Chat className="h-full">
          {/* pin: follows new messages, lets go when the reader scrolls up (DS 2.1.0). */}
          <Chat.MessageList pin>
            {logic.messages.length === 0 && (
              <Chat.Message role="assistant">
                <Chat.MessageAvatar>
                  <Bot className="h-4 w-4" />
                </Chat.MessageAvatar>
                <Chat.MessageBubble>
                  <Chat.MessageContent>
                    Ask me anything about building this workflow — what a step does, why it
                    won&apos;t enable, how to use a value from the event.
                  </Chat.MessageContent>
                </Chat.MessageBubble>
              </Chat.Message>
            )}
            {logic.messages.map((m) => {
              // A question the helper is asking → render the question card.
              if (m.kind === 'question') {
                // Answered questions collapse to one line. Chat.Choices replays
                // the whole list by design, which is right for a form and wrong
                // for a transcript — eight options you already chose from push
                // the live conversation off the screen.
                const answer = m.answered;
                if (answer) {
                  const picked =
                    answer.kind === 'option'
                      ? (m.question.options.find((o) => o.value === answer.value)?.label ??
                        answer.value)
                      : answer.text;
                  return (
                    <Chat.Message key={m.id} role="assistant">
                      <Chat.MessageAvatar>
                        <Bot className="h-4 w-4" />
                      </Chat.MessageAvatar>
                      <div className="min-w-0 rounded-lg border border-outline bg-surface px-3 py-2">
                        <p className="truncate text-xs text-ink-subtle">{m.question.question}</p>
                        <p className="mt-0.5 text-sm font-medium text-ink">{picked}</p>
                      </div>
                    </Chat.Message>
                  );
                }
                return (
                  <Chat.Message key={m.id} role="assistant">
                    <Chat.MessageAvatar>
                      <Bot className="h-4 w-4" />
                    </Chat.MessageAvatar>
                    <Chat.Choices
                      question={m.question.question}
                      options={m.question.options}
                      allowOther={m.question.allowOther}
                      otherPlaceholder={m.question.otherPlaceholder}
                      onSubmit={(answer) => logic.answerQuestion(m.id, answer)}
                    />
                  </Chat.Message>
                );
              }
              // A finished workflow → render the proposal card for review.
              if (m.kind === 'proposal') {
                const warnings = [
                  ...(m.proposal.warnings ?? []),
                  ...m.proposal.issues.map((i) => `Before you can turn it on: ${i}`),
                ];
                return (
                  <Chat.Message key={m.id} role="assistant">
                    <Chat.MessageAvatar>
                      <Bot className="h-4 w-4" />
                    </Chat.MessageAvatar>
                    <Chat.ApprovalCard
                      title={m.proposal.name}
                      description={m.proposal.summary}
                      warnings={warnings.length > 0 ? warnings : undefined}
                      confirmLabel={onApply ? 'Save as a draft' : 'Create draft'}
                      cancelLabel="Keep editing"
                      status={m.status}
                      busy={logic.committingId === m.id}
                      onConfirm={() =>
                        onApply ? logic.applyProposal(m.id, onApply) : logic.commitProposal(m.id)
                      }
                      onCancel={() => logic.dismissProposal(m.id)}
                    >
                      {/* The SAME sentence the editor and the list show, from
                          the same function — you confirm what you will get,
                          not a second rendering that could disagree with it.
                          The model's own prose sits above it as `description`. */}
                      {Array.isArray(m.proposal.definition?.steps) ? (
                        <SummarySentence
                          className="text-sm leading-relaxed text-ink-muted"
                          tokens={summarize(m.proposal.trigger, m.proposal.definition)}
                        />
                      ) : (
                        <p className="text-sm text-ink-muted">
                          The draft isn&apos;t complete yet — see what&apos;s left below.
                        </p>
                      )}
                    </Chat.ApprovalCard>
                  </Chat.Message>
                );
              }
              // Plain prose.
              return (
                <Chat.Message key={m.id} role={m.role}>
                  {m.role === 'assistant' && (
                    <Chat.MessageAvatar>
                      <Bot className="h-4 w-4" />
                    </Chat.MessageAvatar>
                  )}
                  <Chat.MessageBubble>
                    {m.role === 'assistant' ? (
                      <Chat.MessageMarkdown>{m.content}</Chat.MessageMarkdown>
                    ) : (
                      <Chat.MessageContent>{m.content}</Chat.MessageContent>
                    )}
                  </Chat.MessageBubble>
                </Chat.Message>
              );
            })}
            {logic.commitError && (
              <p className="px-3 py-1 text-xs text-danger">
                Couldn&apos;t create the draft — try again.
              </p>
            )}
            {logic.thinking && (
              <Chat.Message role="assistant">
                <Chat.MessageAvatar>
                  <Bot className="h-4 w-4" />
                </Chat.MessageAvatar>
                <Chat.TypingIndicator />
              </Chat.Message>
            )}
            {logic.error && (
              <p className="px-3 py-1 text-xs text-danger">
                That didn&apos;t go through — try again.
              </p>
            )}
          </Chat.MessageList>
          <Chat.Composer
            value={logic.draft}
            onChange={logic.setDraft}
            onSubmit={logic.send}
            disabled={logic.thinking}
          >
            <Chat.ComposerInput placeholder="Ask about the builder…" />
            <div className="flex items-center justify-between">
              <Chat.ComposerToolbar>
                <span className="text-xs text-ink-subtle">Knows the guides + your draft</span>
              </Chat.ComposerToolbar>
              <Chat.ComposerSendButton />
            </div>
          </Chat.Composer>
        </Chat>
      </FloatingLauncher.Panel>
      <FloatingLauncher.Button aria-label="Ask the helper">
        <MessageCircle />
      </FloatingLauncher.Button>
    </FloatingLauncher>
  );
};
